import { useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import { ArrowLeft } from '@phosphor-icons/react';
import { Flex, IconButton, SpinnerFullPage } from '@updater/ui-uds';
import { PageHeading } from '@updater/ui-widgets';
import { PageRoutes } from 'constants/pageRoutes';
import { OffersContext } from 'context';
import { useAcceptUpdaterTerms } from 'hooks';
import { TermsAndPrivacyDisclaimer } from 'components/TermsAndPrivacyDisclaimer';
import { Events, usePageEvent, useTracking } from 'services';
import type { Address } from 'types';
import { AddressEntryForm } from './AddressEntryForm';

export const HAPPY_PATH_HEADING =
  "Perfect! Let's check your address to ensure your plan is available.";
export const NO_OFFERS_HEADING =
  "Well, that didn't work. Let's try with a full address.";

export function AddressEntryPage() {
  const router = useRouter();
  const offersContext = useContext(OffersContext);
  const tracker = useTracking();
  const acceptUpdaterTerms = useAcceptUpdaterTerms();
  usePageEvent(Events.FULL_ADDRESS);

  const isLoadingOffers = offersContext.primaryData.loading;
  const hasOffers =
    !isLoadingOffers &&
    offersContext.primaryData?.offersWithProviders?.length > 0;
  const pageHeading = hasOffers ? HAPPY_PATH_HEADING : NO_OFFERS_HEADING;

  const routeToPlans = useCallback(
    (address: Address) => {
      tracker.trackEvent(Events.FULL_ADDRESS, 'submitted');
      acceptUpdaterTerms(address);
      router.push(PageRoutes.plans);
    },
    [router, tracker, acceptUpdaterTerms]
  );

  if (isLoadingOffers) {
    return <SpinnerFullPage />;
  }

  return (
    <Flex maxWidth="708px" flexDirection="column" gap="s" py="s">
      <IconButton
        onClick={() => router.back()}
        size="s"
        icon={<ArrowLeft />}
        variant="secondary"
        type="button"
        aria-label="Back button"
        data-cy="backButton"
        data-testid="back-button"
      >
        Back
      </IconButton>
      <PageHeading>{pageHeading}</PageHeading>
      <AddressEntryForm onSubmit={routeToPlans} />
      <TermsAndPrivacyDisclaimer mt="l" />
    </Flex>
  );
}
